/* Rendu du ticket de pesée en image, pour impression thermique 58 mm.

   La tête d'impression fait 384 points de large : c'est la seule
   dimension qui compte. Tout est calé sur une grille monospace qui
   remplit cette largeur — le nombre de colonnes fixe donc la taille
   des caractères, et non l'inverse. La colonne des valeurs se déduit
   des colonnes : elle est placée pour qu'un poids de cinq chiffres, sept
   caractères avec son unité, finisse pile sur la marge droite.

   L'image n'est jamais stockée — un ticket se régénère à l'identique
   depuis ses champs, ce qui rend une réimpression gratuite. */

export const LARGEUR = 384      // points de la tête d'impression 58 mm
const COLONNES = 25             // caractères par ligne — commande la taille du texte
const MARGE_X = 12
const MARGE_Y = 20
const INTERLIGNE = 1.35         // hauteur de ligne, en multiples de la police

/* Colonne unique où démarrent toutes les valeurs, balance comprise.
   Calée sur la fin de ligne : changer le nombre de colonnes ne doit pas
   décrocher les poids de la marge droite. */
const LARGEUR_POIDS = 7         // « 09220kg »
const COL_VALEUR = COLONNES - LARGEUR_POIDS

/* La police du ticket d'origine. Absente d'iOS — où le PNG est
   pourtant fabriqué — elle est servie par le site et chargée à
   l'ouverture de l'écran. Menlo prend le relais si le chargement
   échoue : le ticket reste imprimable, avec un trait plus épais. */
/* Nom propre plutôt que « Letter Gothic Std » : la police est installée
   sur le Mac où le ticket a été conçu, et le navigateur y puiserait
   sans jamais toucher au fichier servi. Le rendu serait alors juste
   sur ce poste et faux sur l'iPhone, sans que rien ne le montre. */
export const FAMILLE = 'Ticket Gothic'
const REPLI = 'Menlo, ui-monospace, SFMono-Regular, "DejaVu Sans Mono", monospace'

/* Le ticket ne connaît qu'une graisse : rien n'y est mis en avant, pas
   même le net. */
const police = (px) => `${px}px "${FAMILLE}", ${REPLI}`

let chargement
let chargee = false

/* Idempotent : l'écran peut le demander à chaque montage sans
   retélécharger. */
export function chargerPolice() {
  if (chargement) return chargement
  if (typeof FontFace === 'undefined' || !document.fonts) {
    chargement = Promise.resolve(false)
    return chargement
  }

  const face = new FontFace(FAMILLE, 'url(/fonts/LetterGothicStd.woff2)',
    { weight: '400', style: 'normal' })

  chargement = face.load()
    .then((f) => {
      document.fonts.add(f)
      chargee = true
      return true
    })
    .catch((e) => { console.error('chargement de la police du ticket', e); return false })

  return chargement
}

/* Le PNG doit être fabriqué avec la bonne police, jamais avec le repli
   sans le dire.

   `document.fonts.check` ne sert à rien ici : il répond vrai pour toute
   famille inconnue, faute d'@font-face en attente. On suit donc nous-mêmes
   l'issue du chargement. */
export const policePrete = () => chargee

/* Date au format bascule : 10/08/26. */
export const dateCourte = (iso) => {
  const [a, m, j] = String(iso || '').split('-')
  return a ? `${j}/${m}/${a.slice(2)}` : ''
}

/* Le poids colle à son unité et se complète à cinq chiffres, comme sur
   le ticket de la bascule : 9220 s'y imprime « 09220kg ». Les poids
   s'alignent ainsi tous sur la marge droite. */
const kg = (v) => {
  if (v === '' || v == null || Number.isNaN(Number(v))) return ''
  return `${String(Math.round(v)).padStart(5, '0')}kg`
}

export const netDe = (p1, p2) => {
  const a = Number(p1), b = Number(p2)
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null
  return Math.abs(b - a)
}

/* Une ligne « libellé + valeur » posée sur la grille monospace.

   La grille resserrée laisse peu de place aux valeurs : un nom de
   client un peu long repart à la ligne sous sa colonne plutôt que de
   sortir du papier, où il serait perdu sans que rien ne le signale. */
function paire(label, valeur, col = COL_VALEUR) {
  const v = `${valeur ?? ''}`
  if (!v) return [{ texte: label }]

  const place = COLONNES - col
  if (v.length <= place) return [{ texte: (label.padEnd(col) + v).trimEnd() }]

  return decouper(v, place)
    .map((m, i) => ({ texte: ((i === 0 ? label : '').padEnd(col) + m).trimEnd() }))
}

/* Découpe sur les espaces ; ne tranche dans un mot que s'il est à lui
   seul plus large que la place disponible. */
function decouper(v, place) {
  const lignes = []
  let courante = ''

  for (const mot of v.split(' ')) {
    if (mot.length > place) {
      if (courante) { lignes.push(courante); courante = '' }
      const bouts = mot.match(new RegExp(`.{1,${place}}`, 'g'))
      lignes.push(...bouts.slice(0, -1))
      courante = bouts[bouts.length - 1]
      continue
    }
    const essai = courante ? `${courante} ${mot}` : mot
    if (essai.length <= place) courante = essai
    else { lignes.push(courante); courante = mot }
  }
  if (courante) lignes.push(courante)
  return lignes
}


const SEP = { texte: '-'.repeat(COLONNES) }
const RIEN = { texte: '' }

/* Le ticket, ligne à ligne. Sert au rendu comme à l'aperçu texte. */
export function lignesTicket(tk) {
  return [
    { texte: 'PONT BASCULE' },
    RIEN,
    SEP,
    ...paire('PLAQUE', tk.plaque || ''),
    SEP,
    RIEN,
    ...paire('CLIENT', tk.client || ''),
    ...paire('FOURNISSEUR', tk.fournisseur || ''),
    ...paire('PRODUIT', tk.produit || ''),
    ...paire('VEHICULE', tk.vehicule || ''),
    RIEN,
    ...paire('BALANCE', tk.balance || ''),
    RIEN,
    { texte: `${dateCourte(tk.date_pesee)}  ${tk.heure || ''}`.trim() },
    /* Deux lignes : c'est le blanc du ticket d'origine, qui détache la
       pesée de tout ce qui la décrit. */
    RIEN,
    RIEN,
    ...paire('COMPTEUR', tk.compteur ?? ''),
    ...paire('1.PESEE', kg(tk.poids1)),
    ...paire('2.PESEE', kg(tk.poids2)),
    SEP,
    ...paire('NET', kg(netDe(tk.poids1, tk.poids2))),
  ]
}

/* Cherche la taille de police qui fait tenir exactement une ligne pleine
   dans la largeur utile. measureText est la seule source fiable : la
   métrique d'une monospace n'est pas la même d'un appareil à l'autre,
   et un ticket décalé d'un caractère se voit à l'impression. */
function calibrer(ctx, largeurUtile) {
  let taille = largeurUtile / COLONNES / 0.6
  const gabarit = 'M'.repeat(COLONNES)
  for (let i = 0; i < 6; i++) {
    ctx.font = police(taille)
    const mesure = ctx.measureText(gabarit).width
    if (!mesure) break
    if (Math.abs(mesure - largeurUtile) < 0.25) break
    taille *= largeurUtile / mesure
  }
  return taille
}

/* Dessine le ticket dans un canvas et le redimensionne à la volée.

   `echelle` multiplie la définition du PNG produit : ×1 correspond au
   point d'impression, les valeurs supérieures donnent une image plus
   confortable à relire dans la galerie photo, que l'appli de l'imprimante
   réduit ensuite. */
export function dessinerTicket(canvas, tk, echelle = 2) {
  const ctx = canvas.getContext('2d')
  const utile = LARGEUR - MARGE_X * 2

  // Première passe sur un contexte à l'échelle 1 pour connaître la
  // police, donc la hauteur nécessaire, avant de dimensionner.
  ctx.setTransform(1, 0, 0, 1, 0, 0)
  const taille = calibrer(ctx, utile)
  const lignes = lignesTicket(tk)

  const pas = taille * INTERLIGNE
  const hauteur = MARGE_Y * 2 + lignes.length * pas

  canvas.width = Math.round(LARGEUR * echelle)
  canvas.height = Math.round(hauteur * echelle)
  /* Largeur en points d'impression, hauteur déduite du ratio : sur un
     écran d'iPhone le cadre d'aperçu contraint la largeur, et une
     hauteur figée en pixels déformerait le ticket. */
  canvas.style.width = `${LARGEUR}px`
  canvas.style.height = 'auto'

  ctx.setTransform(echelle, 0, 0, echelle, 0, 0)
  ctx.fillStyle = '#FFFFFF'
  ctx.fillRect(0, 0, LARGEUR, hauteur)
  ctx.fillStyle = '#000000'
  ctx.textBaseline = 'alphabetic'
  ctx.font = police(taille)

  let y = MARGE_Y
  lignes.forEach((l) => {
    y += pas
    if (l.texte) ctx.fillText(l.texte, MARGE_X, y)
  })

  return { largeur: canvas.width, hauteur: canvas.height }
}

/* Nom de fichier lisible dans la galerie photo, où les tickets
   d'une même journée se retrouvent côte à côte. */
export function nomFichier(tk, rang) {
  const cle = tk.compteur ?? String(tk.date_pesee || '').replace(/-/g, '')
  return `ticket-${cle}${rang ? `-${rang}` : ''}.png`
}

/* `canvas.toBlob` est asynchrone, et sur iOS la moindre attente entre le
   clic et l'ouverture de la feuille de partage fait perdre le geste
   utilisateur — le partage est alors refusé. On passe donc par
   `toDataURL`, synchrone, et on décode nous-mêmes. */
function dataUrlVersBlob(dataUrl) {
  const [entete, donnees] = dataUrl.split(',')
  const type = entete.match(/:(.*?);/)?.[1] || 'image/png'
  const binaire = atob(donnees)
  const octets = new Uint8Array(binaire.length)
  for (let i = 0; i < binaire.length; i += 1) octets[i] = binaire.charCodeAt(i)
  return new Blob([octets], { type })
}

/* PNG du ticket, prêt à être partagé ou téléchargé. Synchrone à dessein. */
export function ticketFichier(tk, echelle = 2, rang = 0) {
  const canvas = document.createElement('canvas')
  dessinerTicket(canvas, tk, echelle)
  const blob = dataUrlVersBlob(canvas.toDataURL('image/png'))
  return new File([blob], nomFichier(tk, rang), { type: 'image/png' })
}

/* Un exemplaire, ou deux quand le camion et la remorque sont imprimés
   séparément. Les deux tickets sont identiques : c'est la même pesée. */
export function fichiersTicket(tk, echelle = 2) {
  const n = Number(tk.nb_tickets) === 2 ? 2 : 1
  if (n === 1) return [ticketFichier(tk, echelle)]
  return [ticketFichier(tk, echelle, 1), ticketFichier(tk, echelle, 2)]
}

/* Enregistrement dans la galerie photo.

   Sur iPhone, un lien `download` atterrit dans Fichiers, pas dans
   Photos : il faut passer par la feuille de partage, qui propose
   « Enregistrer sur Photos » et alimente l'appli de l'imprimante.
   Ailleurs — et si le partage de fichiers n'est pas gréé — on
   retombe sur le téléchargement classique.

   Le partage doit suivre le clic sans coupure : les blobs sont donc
   passés déjà générés, jamais fabriqués ici. */
export async function enregistrerTickets(fichiers) {
  const partageable =
    typeof navigator !== 'undefined' &&
    navigator.canShare?.({ files: fichiers }) &&
    typeof navigator.share === 'function'

  if (partageable) {
    try {
      await navigator.share({ files: fichiers })
      return 'partage'
    } catch (e) {
      // L'utilisateur a fermé la feuille de partage : ne rien forcer.
      if (e?.name === 'AbortError') return 'annule'
    }
  }

  fichiers.forEach((f) => {
    const url = URL.createObjectURL(f)
    const a = document.createElement('a')
    a.href = url
    a.download = f.name
    document.body.appendChild(a)
    a.click()
    a.remove()
    setTimeout(() => URL.revokeObjectURL(url), 10000)
  })
  return 'telechargement'
}
